'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  EntityDrawerSections,
  EntityDrawerSection,
} from '../../../components/entity-drawer';
import {
  BadgeStatus,
  BADGE_STATUS,
  FINDING_STATUS_BADGE_STATUS_MAP,
  type BadgeStatusType,
} from '../../../components/badge';
import { TimelineRoot, type TimelineStepData } from '../../timeline';
import { Spinner } from '../../../components/spinner';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../../components/typography';
import { useBranding } from '../../branding/BrandingProvider';
import {
  AlertCircleIcon,
  ArchiveIcon,
  CheckmarkCircle02Icon,
  File01Icon,
  Link01Icon,
  RefreshIcon,
  ScrollIcon,
  Settings01Icon,
  Shield01Icon,
  UserAdd01Icon,
} from '@terra/ui-icons';
import type { FindingAuditLog } from '@terra/shared-contracts-findings';
import { FindingAuditActionSchema } from '@terra/shared-contracts-findings';
import type { ReactNode } from 'react';
import { formatFindingStatusForDisplay } from '../../../utils';
import {
  getFindingAuditActorDisplay,
  resolveRequestedBy,
  toRequestedByActorSource,
} from './finding-audit-actor';
import {
  getFindingAuditDetailText,
  getFindingAuditTitle,
  isActorlessLifecycleAction,
  shouldAppendRequestedByLine,
  usesFindingStatusBadges,
} from './finding-audit-copy';
import {
  formatAuditDateTime,
  formatPreventionStatusLabel,
  formatProviderDisplayName,
  formatRetestOutcomeDisplay,
  parsePreventionAuditValue,
  PREVENTION_AUDIT_ACTIONS,
  readMetadataString,
} from './finding-audit-format';

type FindingDrawerAuditTabProps = {
  auditLogs: FindingAuditLog[];
  isLoading?: boolean;
  hideTitle?: boolean;
  isSeverityChangeRequestEnabled?: boolean;
  dateFormatter: {
    fromString: (date: string) => string;
  };
};

const SEVERITY_CHANGE_REQUEST_AUDIT_ACTIONS = new Set<
  FindingAuditLog['action']
>([
  FindingAuditActionSchema.enum.SEVERITY_CHANGE_REQUESTED,
  FindingAuditActionSchema.enum.SEVERITY_CHANGE_REJECTED,
]);
const getActionIcon = (action: FindingAuditLog['action']) => {
  switch (action) {
    case 'CREATED':
      return <File01Icon size={16} />;
    case 'STATUS_CHANGED':
      return <AlertCircleIcon size={16} />;
    case 'SEVERITY_CHANGED':
    case 'SEVERITY_CHANGE_REQUESTED':
    case 'SEVERITY_CHANGE_REJECTED':
    case 'CVSS_SCORE_CHANGED':
    case 'CVSS_VECTOR_CHANGED':
    case 'OWASP_RISK_LEVEL_CHANGED':
      return <Shield01Icon size={16} />;
    case 'ASSIGNED':
      return <UserAdd01Icon size={16} />;
    case 'EXTERNAL_REFERENCE_ADDED':
    case 'EXTERNAL_REFERENCE_REMOVED':
    case 'EXTERNAL_REFERENCE_UPDATED':
      return <Link01Icon size={16} />;
    case 'PUBLISHED':
      return <CheckmarkCircle02Icon size={16} />;
    case 'ARCHIVED':
      return <ArchiveIcon size={16} />;
    case 'RESTORED':
      return <RefreshIcon size={16} />;
    case 'PREVENTION_CONTROL_STAGED':
    case 'PREVENTION_CONTROL_ACTIVATED':
    case 'PREVENTION_CONTROL_REJECTED':
    case 'PREVENTION_CONTROL_REMOVED':
    case 'PREVENTION_CONTROL_DRIFTED':
      return <Shield01Icon size={16} />;
    default:
      return <Settings01Icon size={16} />;
  }
};

const mapStatusToBadgeType = (status: string): BadgeStatusType => {
  const badgeStatus =
    FINDING_STATUS_BADGE_STATUS_MAP[
      status as keyof typeof FINDING_STATUS_BADGE_STATUS_MAP
    ];

  return badgeStatus ?? BADGE_STATUS.UNRESOLVED;
};

const getStatusBadgeDescription = (
  log: FindingAuditLog,
  t: (key: string) => string,
) => {
  if (log.oldValue && log.newValue) {
    return (
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        <BodyDefault as="span">{t('audit.from')}:</BodyDefault>
        <BadgeStatus status={mapStatusToBadgeType(log.oldValue)} size="sm">
          {formatFindingStatusForDisplay(log.oldValue)}
        </BadgeStatus>
        <BodyDefault as="span">→ {t('audit.to')}:</BodyDefault>
        <BadgeStatus status={mapStatusToBadgeType(log.newValue)} size="sm">
          {formatFindingStatusForDisplay(log.newValue)}
        </BadgeStatus>
      </HStack>
    );
  }

  if (log.newValue) {
    return (
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        <BodyDefault as="span">{t('audit.to')}:</BodyDefault>
        <BadgeStatus status={mapStatusToBadgeType(log.newValue)} size="sm">
          {formatFindingStatusForDisplay(log.newValue)}
        </BadgeStatus>
      </HStack>
    );
  }

  return undefined;
};

const composeDescription = ({
  baseDescription,
  requestedByLine,
  reasonLabel,
  reasonBody,
}: {
  baseDescription: ReactNode | undefined;
  requestedByLine?: string;
  reasonLabel?: string;
  reasonBody?: string;
}): ReactNode | undefined => {
  if (!requestedByLine && !reasonLabel && !reasonBody) {
    return baseDescription;
  }

  return (
    <Box display="flex" flexDirection="column" gap="1">
      {baseDescription}
      {requestedByLine ? (
        <BodyDefault as="span" color="fg.muted">
          {requestedByLine}
        </BodyDefault>
      ) : null}
      {reasonLabel && reasonBody ? (
        <BodyDefault as="span" color="fg.muted">
          <BodyStrong as="span" color="fg.default">
            {reasonLabel}:
          </BodyStrong>{' '}
          {reasonBody}
        </BodyDefault>
      ) : null}
    </Box>
  );
};

const getFromToDetailDescription = ({
  oldValue,
  newValue,
  t,
}: {
  oldValue?: string | null;
  newValue?: string | null;
  t: (key: string) => string;
}): ReactNode | undefined => {
  if (oldValue && newValue) {
    return (
      <BodyDefault as="span">
        {t('audit.from')}: <BodyStrong as="span">{oldValue}</BodyStrong> →{' '}
        {t('audit.to')}: <BodyStrong as="span">{newValue}</BodyStrong>
      </BodyDefault>
    );
  }

  if (newValue) {
    return (
      <BodyDefault as="span">
        {t('audit.to')}: <BodyStrong as="span">{newValue}</BodyStrong>
      </BodyDefault>
    );
  }

  return undefined;
};

const getRichFieldChangeDescription = ({
  log,
  t,
}: {
  log: FindingAuditLog;
  t: (key: string) => string;
}): ReactNode | undefined => {
  if (PREVENTION_AUDIT_ACTIONS.has(log.action)) {
    if (
      log.action === FindingAuditActionSchema.enum.PREVENTION_CONTROL_REJECTED
    ) {
      return undefined;
    }

    const oldParsed = parsePreventionAuditValue(log.oldValue);
    const newParsed = parsePreventionAuditValue(log.newValue);

    return getFromToDetailDescription({
      oldValue: oldParsed
        ? formatPreventionStatusLabel(oldParsed.status)
        : null,
      newValue: newParsed
        ? formatPreventionStatusLabel(newParsed.status)
        : null,
      t,
    });
  }

  if (
    log.action !== FindingAuditActionSchema.enum.SEVERITY_CHANGED &&
    log.action !== FindingAuditActionSchema.enum.SEVERITY_CHANGE_REQUESTED &&
    log.action !== FindingAuditActionSchema.enum.SEVERITY_CHANGE_REJECTED &&
    log.action !== FindingAuditActionSchema.enum.CVSS_SCORE_CHANGED &&
    log.action !== FindingAuditActionSchema.enum.CVSS_VECTOR_CHANGED &&
    log.action !== FindingAuditActionSchema.enum.OWASP_RISK_LEVEL_CHANGED &&
    log.action !== FindingAuditActionSchema.enum.ASSIGNED
  ) {
    return undefined;
  }

  return getFromToDetailDescription({
    oldValue: log.oldValue,
    newValue: log.newValue,
    t,
  });
};

const getRetestCompletedDescription = ({
  outcome,
  summary,
  isFailedOutcome,
}: {
  outcome: string;
  summary: string;
  isFailedOutcome: boolean;
}): ReactNode | undefined => {
  if (isFailedOutcome) {
    return summary || undefined;
  }

  if (outcome && summary) {
    return (
      <BodyDefault as="span" color="fg.neutral.muted">
        <BodyStrong as="span" color="fg.default">
          {outcome}.
        </BodyStrong>{' '}
        {summary}
      </BodyDefault>
    );
  }

  if (summary) {
    return summary;
  }

  return outcome || undefined;
};

export const FindingDrawerAuditTab = ({
  auditLogs,
  isLoading,
  hideTitle = false,
  isSeverityChangeRequestEnabled = false,
  dateFormatter,
}: FindingDrawerAuditTabProps) => {
  const t = useTranslations('designSystem.findings.findingDrawer');
  const { brandName } = useBranding();
  const timelineSteps: TimelineStepData[] = useMemo(() => {
    const filteredLogs = auditLogs.filter((log) => {
      if (
        !isSeverityChangeRequestEnabled &&
        SEVERITY_CHANGE_REQUEST_AUDIT_ACTIONS.has(log.action)
      ) {
        return false;
      }

      if (log.action === FindingAuditActionSchema.enum.REMEDIATION_PR_LINKED) {
        return log.metadata?.linkReason !== 'terra_remediate';
      }

      return true;
    });
    const chronologicalLogs = [...filteredLogs].sort(
      (leftLog, rightLog) =>
        new Date(leftLog.createdAt).getTime() -
        new Date(rightLog.createdAt).getTime(),
    );

    return chronologicalLogs.map((log) => {
      const provider = readMetadataString(log.metadata, 'provider');
      const providerDisplay = provider
        ? formatProviderDisplayName(provider)
        : t('audit.ticketProviderFallback');
      const isExternalReferenceAction =
        log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_ADDED ||
        log.action ===
          FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_REMOVED ||
        log.action === FindingAuditActionSchema.enum.EXTERNAL_REFERENCE_UPDATED;
      const executor = {
        actorType: log.actorType,
        actorId: log.actorId,
        actorName: log.actorName,
        isTerraUser: log.isTerraUser,
        provider:
          PREVENTION_AUDIT_ACTIONS.has(log.action) || isExternalReferenceAction
            ? null
            : provider,
      };
      const actorDisplay = getFindingAuditActorDisplay({
        actor: executor,
        brandName,
        t,
      });
      const requestedBy = resolveRequestedBy({ log, logs: chronologicalLogs });
      const requestedByDisplay = requestedBy
        ? getFindingAuditActorDisplay({
            actor: toRequestedByActorSource(requestedBy, provider),
            brandName,
            t,
          })
        : undefined;
      const title = getFindingAuditTitle({
        log,
        actorDisplay,
        requestedByDisplay,
        providerDisplay,
        t,
      });
      const detailText = getFindingAuditDetailText({
        log,
        requestedByDisplay,
        t,
        formatTimestamp: (value) =>
          formatAuditDateTime({ value, dateFormatter }),
      });
      const retestCompletedRawOutcome =
        log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED
          ? (readMetadataString(log.metadata, 'outcome') ?? log.newValue ?? '')
          : '';
      const retestCompletedOutcome =
        log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED
          ? formatRetestOutcomeDisplay(retestCompletedRawOutcome)
          : '';
      const retestCompletedSummary =
        log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED
          ? (log.note?.trim() ?? '')
          : '';
      const baseDescription = usesFindingStatusBadges(log)
        ? getStatusBadgeDescription(log, t)
        : log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED
          ? getRetestCompletedDescription({
              outcome: retestCompletedOutcome,
              summary: retestCompletedSummary,
              isFailedOutcome: retestCompletedRawOutcome === 'FAILED',
            })
          : (getRichFieldChangeDescription({ log, t }) ?? detailText);
      const appendRequestedBy = shouldAppendRequestedByLine({
        log,
        requestedByDisplay,
        executorDisplay: actorDisplay,
      });
      const requestedByLine =
        appendRequestedBy && requestedByDisplay
          ? t('audit.details.requestedBy', { actor: requestedByDisplay })
          : undefined;
      const normalizedNote =
        typeof log.note === 'string' && log.note.trim().length > 0
          ? log.note.trim()
          : null;
      const skipNote =
        log.action ===
          FindingAuditActionSchema.enum.PREVENTION_CONTROL_REJECTED ||
        log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED;
      const reasonLabel =
        !normalizedNote || skipNote
          ? undefined
          : isActorlessLifecycleAction(log)
            ? t('audit.noteLabel')
            : t('audit.noteBy', { actor: actorDisplay });
      const reasonBody =
        normalizedNote && !skipNote ? normalizedNote : undefined;
      const description = composeDescription({
        baseDescription,
        requestedByLine,
        reasonLabel,
        reasonBody,
      });
      const hasLongDetail =
        Boolean(reasonBody) ||
        (log.action === FindingAuditActionSchema.enum.RETEST_COMPLETED &&
          Boolean(retestCompletedSummary));

      return {
        id: log.id,
        title,
        icon: getActionIcon(log.action),
        details: [
          {
            timestamp: log.createdAt,
            description,
            descriptionLineClamp: hasLongDetail ? false : undefined,
            tooltip: title,
          },
        ],
      };
    });
  }, [auditLogs, brandName, dateFormatter, isSeverityChangeRequestEnabled, t]);

  if (isLoading) {
    return (
      <EntityDrawerSections>
        {!hideTitle ? (
          <EntityDrawerSection title={t('audit.title')}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <Spinner size="lg" />
            </Box>
          </EntityDrawerSection>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <Spinner size="lg" />
          </Box>
        )}
      </EntityDrawerSections>
    );
  }

  if (!timelineSteps || timelineSteps.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="full"
        h="full"
        py={12}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          alignItems="center"
          maxW="md"
        >
          <ScrollIcon size={24} color="var(--colors-fg-muted)" />
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            alignItems="center"
          >
            <BodyStrong color="fg.default">{t('audit.noLogs')}</BodyStrong>
            <MetaDefault color="fg.muted" textAlign="center">
              {t('audit.noLogsDescription')}
            </MetaDefault>
          </Box>
        </Box>
      </Box>
    );
  }

  const timelineDateFormatter = {
    format: (date: Date) => dateFormatter.fromString(date.toISOString()),
  };

  return (
    <EntityDrawerSections>
      {!hideTitle ? (
        <EntityDrawerSection title={t('audit.title')}>
          <TimelineRoot
            steps={timelineSteps}
            dateFormatter={timelineDateFormatter}
            timestampPlacement="outside"
          />
        </EntityDrawerSection>
      ) : (
        <TimelineRoot
          steps={timelineSteps}
          dateFormatter={timelineDateFormatter}
          timestampPlacement="outside"
        />
      )}
    </EntityDrawerSections>
  );
};
